import noop from 'lodash-es/noop';
import memoize from '@stripcash/ui-kit/utils/memoize';

import apiRequestService from '../api/apiRequestService';

import {
  GetUserSettingsServicePayload,
  RequestPromiseProps,
  UserSetting,
} from './types';
import onParseUserSettings from './utils/onParseUserSettings';

type UserSettingsServiceProps = {
  get: (args: GetUserSettingsServicePayload[]) => Promise<any>;
};

const REQUEST_DEBOUNCE_TIME = 100;

const initBulkUserSettingsService = memoize(
  (): UserSettingsServiceProps => {
    let timeout: Nullable<NodeJS.Timeout>;
    let requests: {
      args: GetUserSettingsServicePayload[];
      promise: RequestPromiseProps;
    }[] = [];

    const timeoutFetcher = () => {
      if (!timeout) {
        timeout = setTimeout(() => {
          const currentRequests = [...requests];

          requests = [];
          timeout = null;

          const queryNames = [
            ...new Set(
              currentRequests.reduce(
                (acc, request) => [
                  ...acc,
                  ...request.args.map(({ name }) => name),
                ],
                [] as string[],
              ),
            ),
          ];

          apiRequestService<{ settings: UserSetting[] }>({
            method: '/userSettings',
            query: { names: queryNames },
          })
            .then(({ settings }) => {
              const settingsByRequest: UserSetting[][] = currentRequests
                .map(({ args }) => args)
                .map((argsArray, index) =>
                  argsArray.map((payload) => {
                    const { promise } = currentRequests[index];

                    return onParseUserSettings(promise.reject, {
                      settings,
                      payload,
                    });
                  }),
                );

              currentRequests.forEach(({ promise: { resolve } }, index) => {
                const setting = settingsByRequest[index];

                if (setting.length > 1) {
                  resolve(setting);
                } else {
                  resolve(setting[0]);
                }
              });
            })
            .catch(noop);
        }, REQUEST_DEBOUNCE_TIME);
      }
    };

    return {
      get: (args) =>
        new Promise((resolve, reject) => {
          requests.push({
            promise: { resolve, reject } as RequestPromiseProps,
            args,
          });

          timeoutFetcher();
        }),
    };
  },
  { max: 1 },
);

const bulkUserSettingsService = initBulkUserSettingsService();

export default bulkUserSettingsService;
